import React from 'react';
import { Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useGuardedNavigate } from '@/hooks/useDraftGuard';
import CloseIcon from '@mui/icons-material/Close';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HistoryIcon from '@mui/icons-material/History';
import TuneIcon from '@mui/icons-material/Tune';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import SessionList from '@/components/Session/SessionList';
import NewSessionButton from '@/components/Session/NewSessionButton';
import { useAuthStore } from '@/stores/useAuthStore';
import { hasPermission } from '@/utils/permissions';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  width: number;
}

/**
 * 左侧会话列表容器，包含 SessionList 和 NewSessionButton。
 * 移动端使用 Drawer，桌面端固定显示。
 */
const Sidebar: React.FC<SidebarProps> = ({ open, onClose, width }) => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const navigate = useGuardedNavigate();
  const { pathname } = useLocation();
  const permissions = useAuthStore(s => s.permissions);
  const practice = pathname === '/chat';
  const entries = [
    { label: '练习空间', path: '/chat', page: 'chat', icon: <ForumOutlinedIcon /> },
    { label: '会话中心', path: '/sessions', page: 'conversations', icon: <HistoryIcon /> },
    { label: 'AI Core', path: '/ai-core', page: 'engine', icon: <TuneIcon /> },
    { label: '知识中心', path: '/knowledge', page: 'knowledge', icon: <MenuBookOutlinedIcon /> },
    { label: '用户与授权', path: '/access', page: 'users', icon: <PeopleOutlineIcon /> },
  ].filter(entry => hasPermission(permissions, entry.page) || (entry.path === '/access' && hasPermission(permissions, 'roles')));
  const content = <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, width }}>
    <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box><Typography sx={{ fontSize: 21, fontWeight: 700, color: 'primary.main' }}>深流</Typography><Typography variant="caption" color="text.secondary">DeepFlow</Typography></Box>
      {isMobile && <IconButton aria-label="关闭导航" onClick={onClose} size="small"><CloseIcon /></IconButton>}
    </Box>
    <Box component="nav" aria-label="主导航" sx={{ px: 1.5 }}>
      {[{ label: '工作台', items: entries.filter(e => ['/chat', '/sessions'].includes(e.path)) },
        { label: '系统管理', items: entries.filter(e => !['/chat', '/sessions'].includes(e.path)) }].filter(group => group.items.length).map(group => <React.Fragment key={group.label}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 0.5, mt: 1 }}>{group.label}</Typography>
      <List disablePadding>{group.items.map(entry => <ListItemButton key={entry.path} selected={pathname === entry.path || (entry.path === '/ai-core' && pathname === '/config')} aria-current={pathname === entry.path ? 'page' : undefined}
        onClick={() => { if (navigate(entry.path) && isMobile) onClose(); }} sx={{ borderRadius: 1, mb: 0.5, minHeight: 42 }}>
        <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{entry.icon}</ListItemIcon>
        <ListItemText primary={entry.label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
      </ListItemButton>)}</List></React.Fragment>)}
    </Box>
    {practice && hasPermission(permissions, 'conversations') ? <>
      <Divider sx={{ my: 1.5, mx: 2 }} />
      <Box sx={{ px: 1.5 }}><NewSessionButton /></Box>
      <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, mt: 2, mb: 1 }}>最近会话</Typography>
      <Box sx={{ px: 1.5, flex: 1, overflow: 'auto', minHeight: 0 }}><SessionList /></Box>
    </> : <Box sx={{ flex: 1 }} />}
  </Box>;
  return (
    <>
      {/* 桌面端固定 Sidebar */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: open ? width : 0,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'width 0.3s ease',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        {!isMobile && content}
      </Box>

      {/* 移动端 Drawer */}
      <Drawer
        anchor="left"
        open={open && isMobile}
        onClose={onClose}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
          },
        }}
      >
        {isMobile && content}
      </Drawer>
    </>
  );
};

export default Sidebar;
